import {
  createFileRoute,
  redirect,
  useRouteContext,
  useRouter,
} from "@tanstack/react-router";
import { useState, type JSX } from "react";
import { z } from "zod";

import { graphql } from "@repo/graphql-types/gql";
import { AppointmentTypesPageQueryDocument } from "@repo/graphql-types/graphql";
import Left from "@repo/icons/default/Left";
import Trash from "@repo/icons/default/Trash";

import { AppointmentTypeForm } from "@/components/appointment/type/appointment-type-form";
import { AppointmentTypeFormLegacy } from "@/components/appointment/type/appointment-type-form-legacy";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import {
  MessageDrawerActionButton,
  MessageDrawerActions,
  MessageDrawerBody,
  MessageDrawerRoot,
  MessageDrawerTitle,
} from "@/components/ui/drawer/message-drawer";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { FullPageSpinner } from "@/components/ui/loading/full-page-spinner";
import { PendingComponent } from "@/components/ui/loading/pending-component";
import {
  fetchQuery,
  useGraphQL,
  useGraphQLMutationWithErrorHandler,
  useResetQuery,
} from "@/hooks/use-graphql";
import { AppointmentTypeCategoryEnum } from "@/lib/enums/appointment-type-category-enum";

const GetAppointmentTypeEditPageQuery = graphql(`
  query GetAppointmentTypeEditPageQuery($codUsuario: Int!, $codUsuarioCompromisso: Int!) {
    appointmentType: usuariosCompromissos_by_pk(
      codUsuarioCompromisso: $codUsuarioCompromisso
    ) {
      sistema
      ativo
      category: usuariosCompromissosCategoria {
        codUsuarioCompromissoCategoria
      }
    }
    ...GetAppointmentTypeFormQueryFragment
    ...GetAppointmentTypeFormLegacyQueryFragment
  }
`);

const DeleteAppointmentTypeMutation = graphql(`
  mutation DeleteAppointmentType($input: LivanceApiDeleteUsuarioCompromissoInput!) {
    LivanceApiDeleteUsuarioCompromisso(arg1: $input)
  }
`);

const AppointmentTypePage = (): JSX.Element => {
  const { user } = Route.useRouteContext();
  const { flags } = useRouteContext({ from: "__root__" });

  const showImprovedAppointmentType = flags["ativa-melhorias-tipo-de-atendimento"];

  const router = useRouter();
  const params = Route.useParams();

  const [showDeleteConfirmationDrawer, setShowDeleteConfirmationDrawer] = useState(false);

  const { mutate: deleteAppointmentType } = useGraphQLMutationWithErrorHandler(
    DeleteAppointmentTypeMutation,
  );

  const resetAppointmentTypesQuery = useResetQuery(AppointmentTypesPageQueryDocument);

  const handleDeleteAppointmentType = (): void => {
    setShowDeleteConfirmationDrawer(false);

    deleteAppointmentType(
      { input: { codUsuarioCompromisso: params.appointmentTypeId } },
      {
        onSuccess: () => {
          resetAppointmentTypesQuery();
          router.history.back();
        },
      },
    );
  };

  const queryResult = useGraphQL(GetAppointmentTypeEditPageQuery, {
    codUsuarioCompromisso: Number(params.appointmentTypeId),
    codUsuario: Number(user.codUsuario),
  });

  const { data } = queryResult;

  const appointmentTypeData = data?.appointmentType;

  const isCategoryNotManual =
    appointmentTypeData &&
    appointmentTypeData.category.codUsuarioCompromissoCategoria !==
      Number(AppointmentTypeCategoryEnum.Manual);

  const isAppointmentTypeInternal = Boolean(appointmentTypeData?.sistema);

  const isUndeletable = isCategoryNotManual ?? isAppointmentTypeInternal;

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Editar atendimento" align="center" />
        {isUndeletable ? (
          <div />
        ) : (
          <HeaderButton
            icon={Trash}
            onClick={() => {
              setShowDeleteConfirmationDrawer(true);
            }}
            align="end"
          />
        )}
      </HeaderRoot>
      <MessageDrawerRoot
        icon={Trash}
        variant="secondary"
        open={showDeleteConfirmationDrawer}
        setOpen={setShowDeleteConfirmationDrawer}
      >
        <MessageDrawerTitle>Excluir atendimento</MessageDrawerTitle>
        <MessageDrawerBody>
          Tem certeza de que deseja excluir este atendimento?
        </MessageDrawerBody>
        <MessageDrawerActions>
          <MessageDrawerActionButton onClick={handleDeleteAppointmentType}>
            Excluir
          </MessageDrawerActionButton>
        </MessageDrawerActions>
      </MessageDrawerRoot>
      <Page
        className={
          showImprovedAppointmentType
            ? "pb-0 -mb-[env(safe-area-inset-bottom)]"
            : undefined
        }
      >
        <AsyncDataWrapper {...queryResult}>
          {data && queryResult.isFetchedAfterMount ? (
            showImprovedAppointmentType ? (
              <AppointmentTypeForm data={data} />
            ) : (
              <AppointmentTypeFormLegacy data={data} />
            )
          ) : (
            <FullPageSpinner />
          )}
        </AsyncDataWrapper>
      </Page>
    </>
  );
};

export const Route = createFileRoute(
  "/settings/schedule-options/appointment-types/$appointmentTypeId/",
)({
  parseParams: (params) => ({
    appointmentTypeId: z.int().parse(Number(params.appointmentTypeId)),
  }),
  loader: async ({ context, params }) => {
    const data = await fetchQuery(context, GetAppointmentTypeEditPageQuery, {
      codUsuario: context.user.codUsuario,
      codUsuarioCompromisso: params.appointmentTypeId,
    });

    if (!data.appointmentType?.ativo) {
      redirect({
        to: "/settings/schedule-options/appointment-types",
        throw: true,
      });
    }
  },
  component: AppointmentTypePage,
  pendingComponent: () => <PendingComponent title="Editar atendimento" />,
});
